import React from 'react';
import { useDropzone } from 'react-dropzone';
import { FiUploadCloud, FiX } from 'react-icons/fi';
import {
  FaFilePdf, FaFileWord, FaFileExcel,
  FaFileImage, FaFileArchive, FaFileAlt,
} from 'react-icons/fa';
import { getSize } from 'helpers/utils';
import PropTypes from 'prop-types';
import { CRM_PROJECT_ATTACHMENTS } from 'app_config';

// ── file icon helper ──────────────────────────────────────────────────────────
const getFileIcon = (type = '') => {
  if (type.includes('pdf'))   return <FaFilePdf   size={16} className="tw-text-red-500"    />;
  if (type.includes('word') || type.includes('doc'))
                              return <FaFileWord  size={16} className="tw-text-blue-500"   />;
  if (type.includes('sheet') || type.includes('xls') || type.includes('excel'))
                              return <FaFileExcel size={16} className="tw-text-green-600"  />;
  if (type.includes('image')) return <FaFileImage size={16} className="tw-text-orange-400"/>;
  if (type.includes('zip') || type.includes('rar'))
                              return <FaFileArchive size={16} className="tw-text-purple-500"/>;
  return <FaFileAlt size={16} className="tw-text-slate-400" />;
};

// ── component ─────────────────────────────────────────────────────────────────
const UploadDocument = ({ files, setFiles, fileTypes, note }) => {
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: fileTypes ?? CRM_PROJECT_ATTACHMENTS,
    onDrop: (accepted) =>
      setFiles(accepted.map(f => Object.assign(f, { preview: URL.createObjectURL(f) }))),
  });

  const handleRemove = (path) => setFiles(files.filter(f => f.path !== path));

  const hint = note ?? 'Only .eml, .doc, .docx, .xls, .xlsx, .pdf, .acad — max 500 MB';

  return (
    <div className="tw-flex tw-flex-col tw-gap-2">
      {/* Drop zone */}
      <div
        {...getRootProps()}
        className={[
          'tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2',
          'tw-border-2 tw-border-dashed tw-rounded tw-py-6 tw-px-4',
          'tw-cursor-pointer tw-transition-colors tw-select-none',
          isDragActive
            ? 'tw-border-blue-400 tw-bg-blue-50'
            : 'tw-border-slate-300 tw-bg-slate-50/50 hover:tw-border-blue-400 hover:tw-bg-blue-50/40',
        ].join(' ')}
      >
        <input {...getInputProps()} />
        <FiUploadCloud
          size={24}
          className={isDragActive ? 'tw-text-blue-500' : 'tw-text-slate-400'}
        />
        <p className="tw-text-sm tw-text-slate-500 tw-mb-0">
          {isDragActive ? 'Release to upload' : 'Drop your files here or click to browse'}
        </p>
      </div>

      {/* Hint */}
      <p className="tw-text-xs tw-text-slate-400 tw-mb-0 tw-text-right">{hint}</p>

      {/* Staged file list */}
      {files.length > 0 && (
        <div className="tw-flex tw-flex-col tw-gap-1.5 tw-mt-1">
          {files.map((file) => (
            <div
              key={file.path}
              className="tw-flex tw-items-center tw-gap-2.5 tw-px-3 tw-py-2 tw-bg-white tw-border tw-border-slate-200 tw-rounded"
            >
              {getFileIcon(file.type)}
              <span className="tw-flex-1 tw-min-w-0 tw-text-sm tw-text-slate-700 tw-truncate">
                {file.path}
              </span>
              <span className="tw-text-xs tw-text-slate-400 tw-flex-shrink-0">
                {getSize(file.size)}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(file.path)}
                className="tw-flex tw-items-center tw-justify-center tw-w-6 tw-h-6 tw-rounded-sm tw-text-slate-400 hover:tw-text-red-500 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer tw-transition-colors tw-flex-shrink-0"
              >
                <FiX size={13} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

UploadDocument.propTypes = {
  files:     PropTypes.arrayOf(PropTypes.object).isRequired,
  setFiles:  PropTypes.func.isRequired,
  fileTypes: PropTypes.string,
  note:      PropTypes.string,
};

export default UploadDocument;
